/**
 * Secure Browser Broker — interactive founder login (M-BB, Stage 3).
 *
 * The founder logs into a website ONCE inside a server-side broker
 * browser (for sites with 2FA / captcha). The browser runs headless on
 * the gateway host; the founder sees it via a polled screenshot and
 * drives it via relayed clicks / keystrokes. The resulting session is
 * saved encrypted and agents reuse it later without ever seeing the
 * password.
 *
 * All endpoints are POST with cookie auth (apiJson sends
 * `credentials: 'include'`). The screenshot endpoint returns the PNG as
 * base64 inside JSON (NOT a binary blob), so the caller renders it with
 * `src={`data:image/png;base64,${base64}`}` — no objectURL needed.
 *
 * Mirrors the `credentialsApi` domain-wrapper idiom in ./credentials.ts.
 */
import { apiJson } from './client';

export interface StartLoginResp {
  ok: true;
  /** Opaque broker-session handle threaded through every later call. */
  handle: string;
}

export interface ScreenshotResp {
  ok: true;
  mime: 'image/png';
  /** Base64-encoded PNG frame (no data: prefix). */
  base64: string;
}

export interface OkResp {
  ok: true;
}

// CHAOS Connect (Secure Browser Broker — browser session import, migration
// 0119). A single-use pairing code the founder pastes into the CHAOS Connect
// browser extension. The extension POSTs the live session (cookies +
// storageState) to the gateway, which derives the credential from this code
// server-side (the extension never sends the credential id) and hands the
// payload to the broker — the agent never sees the cookies (Stage-3 invariant).
export interface ConnectCodeResp {
  /** The single-use pairing code to paste into the extension. */
  code: string;
  /** ISO-8601 instant after which the code is no longer accepted. */
  expiresAt: string;
}

// Read-only status of the imported browser session backing a credential.
export interface SessionStatusResp {
  /** True once a non-expired session has been imported for this credential. */
  connected: boolean;
  /** ISO-8601 instant the session was last imported (absent if never). */
  lastSyncedAt?: string;
  /** ISO-8601 instant the stored session expires (absent if none). */
  expiresAt?: string;
}

// CHAOS Connect for every member (migration 0416) — the account OWNER's own
// connected browser sessions. Owner-only on the server (403 otherwise); 404
// while Connect is off. Never carries a cookie or any secret.
export type MyConnectionState = 'connected' | 'expired' | 'disconnected' | 'revoked';

export interface MyConnection {
  credentialId: string;
  credentialName: string;
  /** Host of the credential's site, e.g. www.linkedin.com. */
  site: string | null;
  state: MyConnectionState;
  /** False when the broker could not be asked for the live status. */
  brokerReachable: boolean;
  connectedAt: string | null;
  connectedBy: { userId: string; name: string | null } | null;
  lastSyncedAt: string | null;
  expiresAt: string | null;
  endedAt: string | null;
  /** Why it ended: user_disconnect, disconnect_all, credential_deleted,
   *  ownership_changed, connector_lost_access, connector_removed. */
  endReason: string | null;
  lastUsedAt: string | null;
  lastUsedCard: { id: string; title: string | null } | null;
}

export interface MyConnectionEvent {
  action:
    | 'connect_session_connected'
    | 'connect_session_disconnected'
    | 'connect_session_revoked'
    | 'connect_session_agent_use'
    | 'connect_session_agent_use_denied';
  at: string | null;
  actorKind: string;
  credentialId: string | null;
  reason: string | null;
  cardId: string | null;
}

export interface MyConnectionsResp {
  connections: MyConnection[];
  events: MyConnectionEvent[];
  truncated?: boolean;
}

export interface DisconnectAllResp {
  ok: boolean;
  disconnected: number;
  failed: string[];
  /** Subset of `failed` already blocked and queued for automatic removal. */
  queued: string[];
  /** The account has more website credentials than one request covers. */
  truncated: boolean;
}

export interface ConnectionViewerPerson {
  userId: string;
  name: string | null;
  email: string | null;
}

export interface ConnectionViewersResp {
  /** canSeeNow: still an active member of one of the owner's active Workspaces. */
  viewers: Array<ConnectionViewerPerson & { grantedAt: string | null; canSeeNow: boolean }>;
  candidates: ConnectionViewerPerson[];
  max: number;
}

export interface SharedConnectionsResp {
  owners: Array<{
    accountId: string;
    ownerName: string | null;
    connections: MyConnection[];
    events: MyConnectionEvent[];
    truncated: boolean;
  }>;
}

export const brokerApi = {
  /** Founder 2026-11-01: who else (appointed by the owner) can SEE the history. */
  connectionViewers(): Promise<ConnectionViewersResp> {
    return apiJson<ConnectionViewersResp>('/credentials/connect/viewers');
  },
  addConnectionViewer(userId: string): Promise<OkResp> {
    return apiJson<OkResp>(`/credentials/connect/viewers/${userId}`, { method: 'PUT', json: {} });
  },
  removeConnectionViewer(userId: string): Promise<OkResp> {
    return apiJson<OkResp>(`/credentials/connect/viewers/${userId}`, { method: 'DELETE' });
  },
  /** Read-only: the history of owners who appointed the caller. */
  connectionsSharedWithMe(): Promise<SharedConnectionsResp> {
    return apiJson<SharedConnectionsResp>('/credentials/connect/shared-with-me');
  },
  /** 0416: the account owner's connected browser sessions + recent journal. */
  myConnections(): Promise<MyConnectionsResp> {
    return apiJson<MyConnectionsResp>('/credentials/connect/mine');
  },
  /** 0416: disconnect every browser session on the owner's credentials. */
  disconnectAll(): Promise<DisconnectAllResp> {
    return apiJson<DisconnectAllResp>('/credentials/connect/disconnect-all', {
      method: 'POST',
      json: {},
    });
  },
  /** Open a broker browser for this credential and return its handle. */
  startLogin(id: string): Promise<StartLoginResp> {
    return apiJson<StartLoginResp>(`/credentials/${id}/interactive-login`, {
      method: 'POST',
      json: {},
    });
  },
  /** Grab the current frame of the broker browser as a base64 PNG. */
  screenshot(id: string, handle: string): Promise<ScreenshotResp> {
    return apiJson<ScreenshotResp>(`/credentials/${id}/interactive-screenshot`, {
      method: 'POST',
      json: { handle },
    });
  },
  /** Relay a click at image-natural pixel coords (x, y are ints). */
  click(id: string, handle: string, x: number, y: number): Promise<OkResp> {
    return apiJson<OkResp>(`/credentials/${id}/interactive-click`, {
      method: 'POST',
      json: { handle, x, y },
    });
  },
  /** Relay typed text (a single printable character, or a longer run). */
  type(id: string, handle: string, text: string): Promise<OkResp> {
    return apiJson<OkResp>(`/credentials/${id}/interactive-type`, {
      method: 'POST',
      json: { handle, text },
    });
  },
  /** Relay a non-printable key (Playwright key name, e.g. "Enter"). */
  key(id: string, handle: string, key: string): Promise<OkResp> {
    return apiJson<OkResp>(`/credentials/${id}/interactive-key`, {
      method: 'POST',
      json: { handle, key },
    });
  },
  /** Persist the current (logged-in) session encrypted for agent reuse. */
  save(id: string, handle: string): Promise<OkResp> {
    return apiJson<OkResp>(`/credentials/${id}/interactive-save`, {
      method: 'POST',
      json: { handle },
    });
  },
  /** Tear down the broker browser without saving (best-effort cleanup). */
  close(id: string, handle: string): Promise<OkResp> {
    return apiJson<OkResp>(`/credentials/${id}/interactive-close`, {
      method: 'POST',
      json: { handle },
    });
  },
  /**
   * CHAOS Connect: mint a fresh single-use pairing code for this credential.
   * The founder pastes it into the CHAOS Connect extension, which then hands
   * the logged-in browser session to the broker. Short-TTL + single-use.
   */
  connectCode(id: string): Promise<ConnectCodeResp> {
    return apiJson<ConnectCodeResp>(`/credentials/${id}/connect-code`, {
      method: 'POST',
      json: {},
    });
  },
  /** CHAOS Connect: read the imported-session status for this credential. */
  sessionStatus(id: string): Promise<SessionStatusResp> {
    return apiJson<SessionStatusResp>(`/credentials/${id}/session-status`);
  },
  /** CHAOS Connect: idempotently remove the encrypted browser session. */
  disconnectSession(id: string, opts: { personalOnly?: boolean } = {}): Promise<OkResp> {
    // personalOnly (My connections): a shared password-login session stored
    // for the credential is not the person's connection and is left alone.
    return apiJson<OkResp>(`/credentials/${id}/session${opts.personalOnly ? '?scope=personal' : ''}`, {
      method: 'DELETE',
    });
  },
};
